import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import RecommendedTopicCard from '../../components/education/RecommendedTopicCard';
import RecommendationEmptyState from '../../components/education/RecommendationEmptyState';
import { buildLearningTopicPath } from '../../lib/educationContent';
import { buildTopicRecommendations } from '../../lib/learningRecommendations';

const getQuestionKey = (question) => question.question_key || question.key;

const isEmptyResponse = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === undefined || value === null || value === '';
};

const isClientVisibleQuestion = (question) => question.client_visible !== false;

const formatResponse = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return [
            item.description,
            item.name,
            item.recipient,
            item.amount,
            item.relationship
          ]
            .filter(Boolean)
            .join(' - ');
        }
        return String(item || '');
      })
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
};

const isQuestionVisible = (question, responses, questions) => {
  if (!isClientVisibleQuestion(question)) return false;

  const logic = question.conditional_logic_json;
  if (!logic || Object.keys(logic).length === 0) return true;

  const conditions = Array.isArray(logic.conditions)
    ? logic.conditions
    : logic.question_key
      ? [logic]
      : [];

  if (conditions.length === 0) return true;

  const matchCondition = (condition) => {
    const parent = questions.find((item) => getQuestionKey(item) === condition.question_key);
    if (!parent) return true;

    const actualValue = responses[parent.id];
    const expectedValue = condition.value ?? condition.equals;

    switch (condition.operator || 'equals') {
      case 'not_equals':
        return actualValue !== expectedValue;
      case 'nonempty':
        return !isEmptyResponse(actualValue);
      case 'includes':
        return Array.isArray(actualValue)
          ? actualValue.includes(expectedValue)
          : String(actualValue || '').includes(String(expectedValue || ''));
      case 'equals':
      default:
        return actualValue === expectedValue;
    }
  };

  return logic.match === 'any'
    ? conditions.some(matchCondition)
    : conditions.every(matchCondition);
};

const IntakeSummaryPage = () => {
  const { matterId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isClient = profile?.role === 'client';
  const backPath = isClient ? `/client/matters/${matterId}` : `/matters/${matterId}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [matter, setMatter] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [progress, setProgress] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [topicRecommendations, setTopicRecommendations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.advisor_firm_id || !matterId) return;

      try {
        setLoading(true);
        setError(null);

        // 1. Fetch Core Data
        const [mRes, sRes, qRes, pRes, rRes] = await Promise.all([
          supabase.from('matters').select('*, client:clients(*)').eq('id', matterId).eq('advisor_firm_id', profile.advisor_firm_id).single(),
          supabase.from('intake_sections').select('*').order('sort_order', { ascending: true }),
          supabase.from('intake_questions').select('*').order('sort_order', { ascending: true }),
          supabase.from('matter_intake_progress').select('*').eq('matter_id', matterId).single(),
          supabase.from('matter_intake_responses').select('question_id, response_json').eq('matter_id', matterId)
        ]);

        if (mRes.error || !mRes.data) throw new Error("Plan not found or access denied.");
        
        setMatter(mRes.data);
        const clientVisibleQuestions = (qRes.data || []).filter((question) => question.client_visible !== false);

        setSections(sRes.data || []);
        setQuestions(clientVisibleQuestions);
        setProgress(pRes.data);
        
        const responseMap = {};
        rRes.data?.forEach(r => { responseMap[r.question_id] = r.response_json?.value; });
        setResponses(responseMap);

        // 2. Recommendation Logic
        const [{ data: rules }, { data: topicLinks, error: topicLinksErr }] = await Promise.all([
          supabase.from('education_recommendation_rules').select('*, resource:education_resources(*)'),
          supabase
            .from('education_question_topics')
            .select(`
              question_key,
              context_label,
              immediate_context,
              display_priority,
              topic:education_topics(
                id,
                title,
                slug,
                content_category,
                short_summary,
                why_we_are_asking,
                plain_english_explanation,
                display_priority
              )
            `)
            .eq('is_active', true)
            .order('display_priority', { ascending: true })
        ]);

        if (topicLinksErr) throw topicLinksErr;
        
        if (rules) {
          const matched = [];
          const seenIds = new Set();

          rules.forEach(rule => {
            if (seenIds.has(rule.resource_id)) return;

            const question = clientVisibleQuestions.find(q => (q.question_key || q.key) === rule.question_key);
            if (!question) return;

            const userVal = responseMap[question.id];
            
            let isTriggered = false;
            if (rule.trigger_value === 'any_nonempty') {
              isTriggered = userVal !== undefined && userVal !== null && userVal !== '';
            } else {
              isTriggered = userVal === rule.trigger_value;
            }

            if (isTriggered && rule.resource) {
              matched.push({
                ...rule.resource,
                reason: rule.recommendation_reason
              });
              seenIds.add(rule.resource_id);
            }
          });
          setRecommendations(matched);
        }

        setTopicRecommendations(buildTopicRecommendations({
          questions: clientVisibleQuestions,
          responses: responseMap,
          topicLinks: topicLinks || []
        }));

      } catch (err) {
        console.error('IntakeSummary Load Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [matterId, profile]);

  const missingRequired = useMemo(() => {
    return questions.filter(q => {
      if (!isQuestionVisible(q, responses, questions)) return false;
      const val = responses[q.id];
      return (q.is_required || q.required) && isEmptyResponse(val);
    });
  }, [questions, responses]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500 font-medium">Processing intake data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center">
          <button onClick={() => navigate(backPath)} className="mr-5 p-2.5 rounded-full hover:bg-white text-slate-400 transition-all border border-transparent hover:border-slate-200 shadow-sm">
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Intake Summary Review</h1>
            <p className="text-sm text-slate-500 mt-1 italic">Client: {matter.client?.first_name} {matter.client?.last_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/matters/${matterId}/intake`)} className="flex items-center px-4 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-slate-50 transition-all shadow-sm">
            <SafeIcon icon={FiIcons.FiEdit3} className="mr-2 h-4 w-4" /> Edit Intake
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <SafeIcon icon={FiIcons.FiBookOpen} className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Recommended Learning</h2>
        </div>
        {topicRecommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topicRecommendations.slice(0, 6).map((topic) => (
              <RecommendedTopicCard
                key={topic.id}
                topic={topic}
                onOpen={(selectedTopic) => navigate(buildLearningTopicPath({
                  slug: selectedTopic.slug,
                  matterId,
                  questionKey: selectedTopic.source_question_key
                }))}
              />
            ))}
          </div>
        ) : (
          <RecommendationEmptyState title="No recommended learning yet">
            Relevant learning topics may appear here after the intake has more answers.
          </RecommendationEmptyState>
        )}
      </div>

      {/* Recommendations Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <SafeIcon icon={FiIcons.FiBookOpen} className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Recommended Guidance</h2>
        </div>
        {recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations.map(res => (
              <div key={res.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group flex flex-col h-full">
                <div className="flex justify-between items-start mb-3">
                  <span className="px-2 py-0.5 bg-slate-100 text-[10px] font-bold text-slate-500 uppercase rounded">{res.resource_type}</span>
                  <span className="text-[10px] text-slate-400 flex items-center"><SafeIcon icon={FiIcons.FiClock} className="mr-1" /> {res.estimated_minutes}m</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">{res.title}</h3>
                <p className="text-xs text-slate-500 mb-4 flex-grow line-clamp-2">{res.short_description}</p>
                <div className="pt-4 border-t border-slate-50 mt-auto">
                  <p className="text-[10px] text-blue-600 font-semibold italic mb-3 opacity-80">"{res.reason}"</p>
                  <button onClick={() => navigate(`/education/${res.slug}`)} className="w-full text-center py-2 text-xs font-bold uppercase tracking-widest text-slate-700 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    View Resource
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <RecommendationEmptyState title="No recommended resources yet">
            Helpful resources may appear here when they match this intake.
          </RecommendationEmptyState>
        )}
      </div>

      {/* Missing Required Alert */}
      <div className={`p-6 rounded-xl border-l-4 shadow-sm ${missingRequired.length > 0 ? 'bg-amber-50 border-amber-400' : 'bg-green-50 border-green-400'}`}>
        <div className="flex items-start">
          <SafeIcon icon={missingRequired.length > 0 ? FiIcons.FiAlertTriangle : FiIcons.FiCheckCircle} className={`h-6 w-6 mr-4 ${missingRequired.length > 0 ? 'text-amber-600' : 'text-green-600'}`} />
          <div>
            <h3 className={`text-lg font-bold ${missingRequired.length > 0 ? 'text-amber-800' : 'text-green-800'}`}>
              {missingRequired.length > 0 ? `${missingRequired.length} Required Answers Missing` : 'Questionnaire Complete'}
            </h3>
            {missingRequired.length > 0 && (
              <ul className="mt-3 space-y-2">
                {missingRequired.slice(0, 3).map(q => (
                  <li key={q.id} className="text-sm text-amber-700 font-medium list-disc ml-4">{q.question_text}</li>
                ))}
                {missingRequired.length > 3 && <li className="text-xs text-amber-600 italic ml-4">And {missingRequired.length - 3} more...</li>}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Section List (Condensed version for review) */}
      <div className="space-y-6">
        {sections.map(section => {
          const sectionQs = questions.filter(q => q.section_id === section.id && isQuestionVisible(q, responses, questions));
          if (sectionQs.length === 0) return null;

          return (
            <div key={section.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">{section.title}</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {sectionQs.map(q => (
                  <div key={q.id} className="p-5 flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">{q.question_text}</p>
                      <p className="text-sm text-slate-600 mt-1">
                        {formatResponse(responses[q.id]) || <span className="text-slate-400 italic">Not provided</span>}
                      </p>
                    </div>
                    {(q.is_required || q.required) && isEmptyResponse(responses[q.id]) && (
                      <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest bg-red-50 px-2 py-1 rounded self-start">Missing</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="text-center pt-8 border-t border-slate-200">
        <p className="text-[10px] text-slate-400 italic">Financial advisors do not provide legal advice through this platform.</p>
      </div>
    </div>
  );
};

export default IntakeSummaryPage;
